import { supabase } from './supabaseClient.ts';
import { getAuthUser, setOnSignInCallback } from './AuthContext.tsx';
import { GameType } from '../types/GameType.ts';
import { getGameConfig } from '../types/GameConfig.ts';
import { getNumDaysSinceEpoch } from '../utils/utils.js';
import {
  WeaverGameStats,
  WeaverUserStats,
  SubtextGameStats,
  setWeaverUserStats,
} from '../utils/userStatsUtils.ts';

interface CloudGameStatsRow {
  game_type: string;
  game_number: number;
  stats: Record<string, unknown>;
}

interface GameStatsMap {
  [gameNumber: number]: WeaverGameStats | SubtextGameStats;
}

/**
 * Fetches all game_stats rows for the current user from Supabase.
 */
async function fetchCloudGameStats(): Promise<CloudGameStatsRow[]> {
  const user = getAuthUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('game_stats')
    .select('game_type, game_number, stats')
    .eq('user_id', user.id);

  if (error) {
    console.error('Failed to fetch cloud game stats:', error);
    return [];
  }

  return data || [];
}

/**
 * Reads the gameStats_<gameType> map from localStorage.
 */
function getLocalGameStatsMap(gameType: GameType): GameStatsMap {
  // Try new format first, then old format for Weaver
  const key = `gameStats_${gameType}`;
  let stored = localStorage.getItem(key);

  if (stored === null && gameType === GameType.WEAVER) {
    stored = localStorage.getItem('gameStats');
  }

  if (!stored) return {};

  try {
    return JSON.parse(stored) as GameStatsMap;
  } catch {
    return {};
  }
}

/**
 * Writes the merged gameStats map to localStorage.
 */
function setLocalGameStatsMap(gameType: GameType, statsMap: GameStatsMap): void {
  const json = JSON.stringify(statsMap);
  localStorage.setItem(`gameStats_${gameType}`, json);

  // Backwards compat for Weaver
  if (gameType === GameType.WEAVER) {
    localStorage.setItem('gameStats', json);
  }
}

/**
 * Merges a single game entry using "most progress wins".
 * For Weaver: shorter minPathLength wins.
 * For Subtext: more words wins.
 */
export function mergeGameEntry(
  gameType: GameType,
  local: WeaverGameStats | SubtextGameStats | undefined,
  cloud: Record<string, unknown> | undefined
): WeaverGameStats | SubtextGameStats | undefined {
  if (!local && !cloud) return undefined;
  if (!local) return cloud as WeaverGameStats | SubtextGameStats;
  if (!cloud) return local;

  if (gameType === GameType.SUBTEXT) {
    const localSubtext = local as SubtextGameStats;
    const cloudSubtext = cloud as SubtextGameStats;
    const localWords = localSubtext.words || [];
    const cloudWords = cloudSubtext.words || [];
    // Keep whichever has more words found
    return localWords.length >= cloudWords.length ? localSubtext : cloudSubtext;
  }

  // Weaver / Weaver X: shorter path wins
  const localWeaver = local as WeaverGameStats;
  const cloudWeaver = cloud as WeaverGameStats;
  const localLen = localWeaver.minPathLength ?? Infinity;
  const cloudLen = (cloudWeaver.minPathLength as number) ?? Infinity;
  return localLen <= cloudLen ? localWeaver : cloudWeaver;
}

/**
 * Recomputes WeaverUserStats from the full gameStats map.
 * Derives: numSolved, currentStreak, longestStreak, lastSolvedEpochDayStats.
 */
export function recomputeWeaverUserStats(
  gameType: GameType.WEAVER | GameType.WEAVER_X,
  statsMap: GameStatsMap
): WeaverUserStats {
  const gameConfig = getGameConfig(gameType);
  const currentEpochDay = getNumDaysSinceEpoch();

  // Collect all solved epoch days with their stats
  const solvedDays: { epochDay: number; minPathLength: number; minPathWords: string[] }[] = [];

  for (const [gameNumberStr, stats] of Object.entries(statsMap)) {
    const gameNumber = parseInt(gameNumberStr);
    const weaverStats = stats as WeaverGameStats;
    if (weaverStats.minPathWords != null && weaverStats.minPathLength != null) {
      const epochDay = gameConfig.getEpochDayFromGameNumber(gameNumber);
      solvedDays.push({
        epochDay,
        minPathLength: weaverStats.minPathLength,
        minPathWords: weaverStats.minPathWords,
      });
    }
  }

  if (solvedDays.length === 0) {
    return {
      numSolved: 0,
      currentStreak: 0,
      longestStreak: 0,
      lastSolvedEpochDayStats: null,
    };
  }

  // Sort by epoch day ascending
  solvedDays.sort((a, b) => a.epochDay - b.epochDay);

  // Calculate streaks
  let longestStreak = 1;
  let currentRunLength = 1;

  for (let i = 1; i < solvedDays.length; i++) {
    if (solvedDays[i].epochDay === solvedDays[i - 1].epochDay + 1) {
      currentRunLength++;
    } else {
      currentRunLength = 1;
    }
    longestStreak = Math.max(longestStreak, currentRunLength);
  }

  // Current streak: count backwards from today (or yesterday)
  const lastSolved = solvedDays[solvedDays.length - 1];
  let currentStreak = 0;

  if (lastSolved.epochDay >= currentEpochDay - 1) {
    // Streak is still alive (solved today or yesterday)
    currentStreak = 1;
    for (let i = solvedDays.length - 2; i >= 0; i--) {
      if (solvedDays[i].epochDay === solvedDays[i + 1].epochDay - 1) {
        currentStreak++;
      } else {
        break;
      }
    }
  }

  return {
    numSolved: solvedDays.length,
    currentStreak,
    longestStreak,
    lastSolvedEpochDayStats: {
      epochDay: lastSolved.epochDay,
      minPathLength: lastSolved.minPathLength,
      minPathWords: lastSolved.minPathWords,
    },
  };
}

/**
 * Called on login. Fetches cloud data, merges with localStorage, writes merged result to both.
 */
export async function pullAndMerge(): Promise<void> {
  const user = getAuthUser();
  if (!user) return;

  const cloudRows = await fetchCloudGameStats();

  // Group cloud data by game type
  const cloudByType: Record<string, Record<number, Record<string, unknown>>> = {};
  for (const row of cloudRows) {
    if (!cloudByType[row.game_type]) {
      cloudByType[row.game_type] = {};
    }
    cloudByType[row.game_type][row.game_number] = row.stats;
  }

  // Merge each game type
  const allGameTypes = Object.values(GameType);
  const upsertRows: { user_id: string; game_type: string; game_number: number; stats: Record<string, unknown>; updated_at: string }[] = [];

  for (const gameType of allGameTypes) {
    const localMap = getLocalGameStatsMap(gameType);
    const cloudMap = cloudByType[gameType] || {};

    // Union of all game numbers
    const allGameNumbers = new Set([
      ...Object.keys(localMap).map(Number),
      ...Object.keys(cloudMap).map(Number),
    ]);

    const mergedMap: GameStatsMap = {};
    for (const gameNumber of allGameNumbers) {
      const merged = mergeGameEntry(gameType, localMap[gameNumber], cloudMap[gameNumber]);
      if (merged) {
        mergedMap[gameNumber] = merged;

        // If local and cloud differ, or cloud didn't have this entry, push to cloud
        const cloudEntry = cloudMap[gameNumber];
        if (!cloudEntry || JSON.stringify(merged) !== JSON.stringify(cloudEntry)) {
          upsertRows.push({
            user_id: user.id,
            game_type: gameType,
            game_number: gameNumber,
            stats: merged as Record<string, unknown>,
            updated_at: new Date().toISOString(),
          });
        }
      }
    }

    // Write merged result to localStorage
    setLocalGameStatsMap(gameType, mergedMap);

    // Recompute user stats for Weaver game types
    if (gameType === GameType.WEAVER || gameType === GameType.WEAVER_X) {
      const recomputed = recomputeWeaverUserStats(gameType, mergedMap);
      setWeaverUserStats(gameType, recomputed);
    }
    // Subtext user stats are already derived on-the-fly from gameStats in subtextDeriveUserStats()
  }

  // Push any differences back to cloud
  if (upsertRows.length > 0) {
    const { error } = await supabase
      .from('game_stats')
      .upsert(upsertRows, { onConflict: 'user_id,game_type,game_number' });

    if (error) {
      console.error('Failed to push merged stats to cloud:', error);
    }
  }
}

/**
 * Pushes all localStorage game stats to cloud. Called on first login (new account)
 * or when coming back online.
 */
export async function fullSyncPush(): Promise<void> {
  const user = getAuthUser();
  if (!user) return;

  const allGameTypes = Object.values(GameType);
  const upsertRows: { user_id: string; game_type: string; game_number: number; stats: Record<string, unknown>; updated_at: string }[] = [];

  for (const gameType of allGameTypes) {
    const localMap = getLocalGameStatsMap(gameType);
    for (const [gameNumberStr, stats] of Object.entries(localMap)) {
      upsertRows.push({
        user_id: user.id,
        game_type: gameType,
        game_number: parseInt(gameNumberStr),
        stats: stats as Record<string, unknown>,
        updated_at: new Date().toISOString(),
      });
    }
  }

  if (upsertRows.length > 0) {
    const { error } = await supabase
      .from('game_stats')
      .upsert(upsertRows, { onConflict: 'user_id,game_type,game_number' });

    if (error) {
      console.error('Failed to push local stats to cloud:', error);
    }
  }
}

// Register the sync callback with AuthContext
setOnSignInCallback(pullAndMerge);
